import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../components/Reveal';
import { GridRails } from '../components/GridRails';
import { GridDivider } from '../components/GridDivider';
import type { LegalDoc } from '../data/legal';
import { legalDocs } from '../data/legal';
import { usePageMeta } from '../hooks/usePageMeta';
import { legalSeoDescription } from '../data/seo';
type Props = {
  doc: LegalDoc;
};
const inner = 'relative z-10 mx-auto max-w-[1720px] px-5 md:px-16 lg:px-24';
const label = 'font-accent text-[11px] uppercase tracking-[0.08em]';
const anchor = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

/** Simple text page for Imprint, Privacy, Cookies and Terms. */
export function Legal({
  doc
}: Props) {
  usePageMeta({
    title: `${doc.label} | KAYS & KAYS`,
    description: legalSeoDescription(doc.label)
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);
  return <>
      {/* Navy band under the header */}
      <section className="relative bg-navy pb-16 pt-[180px] text-cream md:pb-20 md:pt-[220px]" data-id="element-452" data-mp="27klj6klqlkh0">
        <div className={inner} data-id="element-453" data-mp="23k2psm4746cp">
          <Reveal data-id="element-454" data-mp="23irn6pmhl4n6">
            <p className={`${label} flex items-center gap-3 text-cream/70`} data-id="element-455" data-mp="23j9tkf2v4z8t">
              <span aria-hidden="true" className="inline-block h-[6px] w-[6px] bg-cream" data-id="element-456" data-mp="2hhzq9tlrswwn" />
              Legal
            </p>
            <h1 className="mt-6 font-display text-[44px] leading-[0.98] tracking-[-0.035em] md:text-[72px]" data-id="element-457" data-mp="2ci4wniam1t7w">
              {doc.title} <span className="font-editorial italic tracking-[-0.015em]" data-id="element-458" data-mp="1jvk8srtcfz93">{doc.titleAccent}</span>
            </h1>
            <p className="mt-6 max-w-[560px] text-[14.5px] leading-[1.55] text-cream/65" data-id="element-459" data-mp="1ykyhdxp3icnf">{doc.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-white text-ink" data-id="element-460" data-mp="1tsf3ezmhxfi3">
        <GridRails data-id="element-461" data-mp="163m7321yp5my" />
        <div className={`${inner} grid gap-12 px-9 py-16 md:grid-cols-[220px_1fr] md:gap-20 md:py-24`} data-id="element-462" data-mp="162ukggqcei8t">
          {/* Contents + other documents */}
          <aside className="md:sticky md:top-12 md:self-start" data-id="element-463" data-mp="317bsehix5i9u">
            <Reveal data-id="element-464" data-mp="316shvlvllw5u">
            <p className={`${label} text-ink/50`} data-id="element-465" data-mp="3157nbw9aad7z">Last updated</p>
            <p className="mt-2 text-[13px] text-ink/80" data-id="element-466" data-mp="3653shytpwap4">{doc.updated}</p>

            <p className={`${label} mt-10 hidden text-ink/50 md:block`} data-id="element-467" data-mp="3awvavm2pza46">Contents</p>
            <ul className="mt-4 hidden space-y-2 text-[13px] md:block" data-id="element-468" data-mp="3eygv5shl2qoe">
              {doc.sections.map(s => <li key={s.heading} data-id="element-469" data-mp="2i97vclgb39oe">
                  <a href={`#${anchor(s.heading)}`} className="link-soft no-arrow text-ink/70" data-id="element-470" data-mp="2iaalgr5t1839">
                    {s.heading}
                  </a>
                </li>)}
            </ul>

            <p className={`${label} mt-10 text-ink/50`} data-id="element-471" data-mp="2r7tae6mkk6kf">Documents</p>
            <ul className="mt-4 space-y-2 text-[13px]" data-id="element-472" data-mp="2w9a6zpy0t6v7">
              {legalDocs.map(d => <li key={d.slug} data-id="element-473" data-mp="6hvju51hbli4">
                  {d.slug === doc.slug ? <span className="flex items-center gap-2 text-ink" data-id="element-474" data-mp="bhgs562jh67i">
                      <span aria-hidden="true" className="inline-block h-[5px] w-[5px] bg-ink" data-id="element-475" data-mp="bjt6l9938yft" />
                      {d.label}
                    </span> : <Link to={`/${d.slug}`} className="link-soft text-ink/70" data-id="element-476" data-mp="gnlvfgyc9ype">
                      {d.label}
                    </Link>}
                </li>)}
            </ul>
            </Reveal>
          </aside>

          <article className="max-w-[680px]" data-id="element-477" data-mp="2vys4x5zbpx4">
            {doc.sections.map((s, i) => <section key={s.heading} id={anchor(s.heading)} className={`scroll-mt-12 ${i > 0 ? 'mt-10 border-t border-mist-line pt-10' : ''}`} data-id="element-478" data-mp="3u176yyom3cev" data-mpi={i}>
                <Reveal data-id="element-479" data-mp="3l1tvy1kuafs1">
                  <h2 className={`${label} flex items-center gap-3 text-ink/80`} data-id="element-480" data-mp="3l2dar9f70o1u">
                    <span className="tabular-nums text-ink/40" data-id="element-481" data-mp="1na92py3kau5k">{String(i + 1).padStart(2, '0')}</span>
                    {s.heading}
                  </h2>
                  <div className="mt-4 space-y-3 text-[14.5px] leading-[1.6] text-ink/70" data-id="element-482" data-mp="1qcvqbpxmeiuy">
                    {s.body.map(p => <p key={p} data-id="element-483" data-mp="1vc8cs9kkxnx0">{p}</p>)}
                  </div>
                </Reveal>
              </section>)}
            <p className="mt-14 text-[13px] text-ink/50" data-id="element-484" data-mp="1hea0d9ptk1k5">
              <Link to="/" className="link-soft no-arrow text-ink/70" data-id="element-485" data-mp="1lcr89vh2dg2m">
                ← Back to KAYS &amp; KAYS
              </Link>
            </p>
          </article>
        </div>
      </section>
      <GridDivider data-id="element-486" data-mp="1ce5t8aaswj6l" />
    </>;
}